import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HttpService } from 'services/HttpService';
import { TokenService } from 'services/TokenService';
import CookieService from 'services/CookieService';
import AnalyticsService from 'services/AnalyticsService';
import { CircleLoader } from 'components/CircleLoader/CircleLoader';
import { MessageBox } from 'components/MessageBox/MessageBox';
import Checkbox from 'components/Checkbox/Checkbox';
import { load } from 'recaptcha-v3'
import './SignUpForm.scss';

/* The sign-up API rejects empty companyName and jobFunction, but the form no
   longer collects either. Both fall back to this sentinel so the data reads as
   honestly absent rather than as a real value. */
const NOT_PROVIDED = 'Not provided';

const FREE_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'mail.com',
  'yandex.com',
  'zoho.com',
];

const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', isMet: (password) => password.length >= 8 },
  { id: 'number', label: 'Include a number', isMet: (password) => /\d/.test(password) },
  { id: 'case', label: 'Use upper and lower case', isMet: (password) => /[a-z]/.test(password) && /[A-Z]/.test(password) },
];

/* Shared by the inline message under the checklist and the MessageBox raised by
   the submit guard, so the two never drift apart. */
const PASSWORD_ERROR_MESSAGE = 'Your password does not meet all the requirements listed below the field.';

export class SignUpPageForm extends Component {
  messageBox = {
    title: 'Sign up request failed',
    message: 'Please choose a different email',
    color: 'danger',
    icon: 'error',
  }

  checkbox = {
    id: "signup-checkbox",
    text: "",
    color: "primary",
    classes: "margin-t-0 margin-b-0"
  }

  constructor(props) {
    super(props);
    this.handleChangeCheckbox = this.handleChangeCheckbox.bind(this);
    this.state = {
      fullName: '', email: '', password: '',
      showPassword: false,
      passwordTouched: false,
      loaderIsActive: false,
      isChecked: false,
      showMessageBox: false,
      activation: false,
    };
  }

  componentWillMount() {
    const queryParams = new URLSearchParams(window.location.search);
    const activation = queryParams.get('a');
    if (activation) {
      this.setState({ activation: activation }, () => {
        this.handleSubmit({ preventDefault: () => { } });
      });
    }
  }

  /* The sign-up API rejects an empty companyName, but the form only collects
     name, work email and password — so derive the company from the email
     domain (jim@acme.com -> Acme). Free-mail domains are not companies, so
     they fall back to the sentinel rather than sending "Gmail" downstream. */
  getCompanyFromEmail = () => {
    const domain = ((this.state.email || '').split('@')[1] || '').toLowerCase();
    const name = domain.split('.')[0] || '';

    if (!name || FREE_EMAIL_DOMAINS.includes(domain)) {
      return NOT_PROVIDED;
    }

    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  isPasswordValid = () => PASSWORD_RULES.every((rule) => rule.isMet(this.state.password || ''))

  getNameFields = () => {
    const parts = (this.state.fullName || '').trim().split(/\s+/).filter(Boolean);
    const [firstName = '', ...rest] = parts;

    return { firstName, lastName: rest.join(' ') };
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    /* The button is disabled until the password qualifies, but guard here too:
       the form can still be submitted with Enter. */
    if (!this.state.activation && !this.isPasswordValid()) {
      this.setState({ passwordTouched: true });
      return this.printErrorMessage({ "message": PASSWORD_ERROR_MESSAGE });
    }

    if (!this.state.activation && !this.state.isChecked) {
      return this.printErrorMessage({ "message": "You must agree to the Privacy Policy and Terms of Service to continue." });
    }

    // Initialize reCAPTCHA
    const recaptcha = await load(process.env.REACT_APP_CAPTCHA_KEY);
    const captchaToken = await recaptcha.execute('submit');

    this.setState({ loaderIsActive: true, showMessageBox: false });
    const { email, password, activation } = this.state;
    const { firstName, lastName } = this.getNameFields();
    const company = this.getCompanyFromEmail();
    const jobFunction = NOT_PROVIDED;
    const host = process.env.REACT_APP_SERVER;
    const body = {
      firstName,
      lastName,
      jobFunction,
      email,
      password,
      companyName: company,
      phoneNumber: '0000000',
      captchaToken,
      activation,
      plan: this.props.plan || 'junior'
    };
    new HttpService().sendRequest(`${host}/api/users/signup/checkout`, {
      body: JSON.stringify(body),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((result) => {
      if (result.hasOwnProperty('url')) {
        window.location.href = result.url;
      } else if (result.hasOwnProperty('token')) {
        this.onSuccessRequest(result);
        this.setState({ loaderIsActive: false });
      } else {
        this.printErrorMessage(result);
      }
    }).catch((err, code) => {
      this.printErrorMessage(err);
    });
    event.preventDefault();
  }

  printErrorMessage = (result) => {
    const { message } = result;
    this.messageBox.message = message || ' Email or Password incorrect.';
    if (typeof message === 'object' && message.hasOwnProperty('errors')) {
      const { errors } = message;
      const messageText = errors.map((item) => item.msg).join('\n');
      this.messageBox.message = messageText;
    }

    this.setState({ showMessageBox: true, loaderIsActive: false });
  }

  onSuccessRequest = (result) => {
    this.setState({ token: result.token });
    const tokenService = new TokenService();
    const cookieService = new CookieService();
    localStorage.setItem('userData', JSON.stringify(result));
    cookieService.setCookie({ name: 'user_type', value: result.userType }, 10);
    AnalyticsService.setUserId(result.email);
    tokenService.saveToken(result.token);
    this.saveLoginCookies();
    this.props.onSubmit(result);
  }

  saveLoginCookies = () => {
    const { token, email, rememberMe } = this.state;
    let cookies = [
      {
        name: 'token',
        value: token,
      },
    ];

    if (rememberMe) {
      cookies = [...cookies, {
        name: 'email',
        value: email,
      }];
    }

    new CookieService().setCookies(cookies, 30);
  }

  onMessageClose = (event) => {
    this.setState({ showMessageBox: false });
  }

  onFullNameChange = (event) => {
    this.setState({ fullName: event.target.value });
  }

  onEmailChange = (event) => {
    this.setState({ email: event.target.value });
  }

  onPasswordChange = (event) => {
    this.setState({ password: event.target.value, passwordTouched: true });
  }

  togglePasswordVisibility = () => {
    this.setState((state) => ({ showPassword: !state.showPassword }));
  }

  handleChangeCheckbox(value) {
    this.setState({ isChecked: value });
  }

  render() {
    const { fullName, email, password, showPassword, loaderIsActive, activation, passwordTouched } = this.state;
    const passwordIsValid = this.isPasswordValid();

    return (
      <form className="o-signup-form" onSubmit={this.handleSubmit}>
        <h1 className="o-signup-form__title">Start your free trial</h1>
        <p className="o-signup-form__subtitle">7 days free. Cancel any time.</p>

        <div className="o-signup-form__field">
          <label className="o-signup-form__label" htmlFor="signup-full-name">Full Name</label>
          <div className="o-signup-form__input-box">
            <i className="material-icons">person_outline</i>
            <input
              id="signup-full-name"
              className="o-signup-form__input"
              type="text"
              autoComplete="name"
              placeholder="Enter your full name"
              value={fullName}
              onChange={this.onFullNameChange}
              required
            />
          </div>
        </div>

        <div className="o-signup-form__field">
          <label className="o-signup-form__label" htmlFor="signup-email">Email Address</label>
          <div className="o-signup-form__input-box">
            <i className="material-icons">mail_outline</i>
            <input
              id="signup-email"
              className="o-signup-form__input"
              type="email"
              autoComplete="email"
              placeholder="name@company.com"
              value={email}
              onChange={this.onEmailChange}
              required
            />
          </div>
        </div>

        <div className="o-signup-form__field">
          <label className="o-signup-form__label" htmlFor="signup-password">Password</label>
          <div className="o-signup-form__input-box">
            <i className="material-icons">lock_outline</i>
            <input
              id="signup-password"
              className="o-signup-form__input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={this.onPasswordChange}
              required
            />
            <button
              type="button"
              className="o-signup-form__reveal"
              onClick={this.togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className="material-icons">{showPassword ? 'visibility_off' : 'visibility'}</i>
            </button>
          </div>
          <ul className="o-signup-form__rules">
            {PASSWORD_RULES.map((rule) => {
              const isMet = rule.isMet(password);

              return (
                <li className={isMet ? 'o-signup-form__rule o-signup-form__rule--is-met' : 'o-signup-form__rule'} key={rule.id}>
                  <i className="material-icons">{isMet ? 'check_circle' : 'radio_button_unchecked'}</i>
                  <span>{rule.label}</span>
                </li>
              );
            })}
          </ul>
          {
            passwordTouched && !passwordIsValid ?
              <p className="o-signup-form__error" role="alert">
                {PASSWORD_ERROR_MESSAGE}
              </p>
              : ''
          }
        </div>

        <div className="o-signup-form__consent">
          <Checkbox {...this.checkbox} handleChange={this.handleChangeCheckbox} />
          {/* Checkbox renders its own label with an empty text, so this second
              label is what gives the input its accessible name and makes the
              sentence clickable. Its `for` matches Checkbox's input id. */}
          <label className="o-signup-form__consent-text" htmlFor="signup-checkbox__checkbox_id">
            I agree to the <a href="https://enkiai.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {' '}and <a href="https://enkiai.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
          </label>
        </div>

        {
          !loaderIsActive && !activation ?
            <button className="o-signup-form__submit" type="submit" value="Submit" disabled={!passwordIsValid}>
              <span>Create account</span>
            </button>
            : <CircleLoader isActive={loaderIsActive} />
        }

        <div className="o-signup-form__login">
          <span>Already have an account?</span>
          <Link to="/login">
            Log in
            <i className="material-icons">arrow_forward</i>
          </Link>
        </div>

        {
          this.state.showMessageBox ?
            <MessageBox {...this.messageBox} onClose={this.onMessageClose} />
            : ''
        }
      </form>
    );
  }
}

export default SignUpPageForm;
